import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Comparison from '../components/Comparison';
import FAQ from '../components/FAQ';
import { Check, ArrowRight, Sparkles, Lock } from 'lucide-react';
import { pricingTiers } from '../mock';
import { startCheckout } from '../lib/checkout';
import { usePricingEnabled } from '../lib/usePricingEnabled';
import TrustBadges from '../components/TrustBadges';

const PricingCard = ({ tier, billing, pricingEnabled }) => {
  const displayPrice = billing === 'annual' ? Math.round(tier.price * 0.8) : tier.price;
  return (
    <div className={`relative flex flex-col rounded-2xl p-7 border transition-all ${
      tier.highlight
        ? 'bg-stone-900 text-white border-stone-900 shadow-2xl md:-translate-y-4'
        : 'bg-white text-stone-900 border-stone-200 hover:shadow-lg'
    }`}>
      {tier.badge && (
        <span className={`absolute -top-3 left-6 text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-full ${
          tier.highlight ? 'bg-[#F97316] text-white' : 'bg-[#4F46E5] text-white'
        }`}>
          {tier.badge}
        </span>
      )}
      <h3 className="text-[22px] font-extrabold" style={{ fontFamily: 'Plus Jakarta Sans' }}>{tier.name}</h3>
      <p className={`text-[13px] mt-1 ${tier.highlight ? 'text-stone-300' : 'text-stone-500'}`}>{tier.tagline}</p>

      <div className="mt-5 flex items-baseline gap-1">
        <span className="text-[42px] font-extrabold" style={{ fontFamily: 'Plus Jakarta Sans' }}>${displayPrice}</span>
        <span className={`text-[13px] ${tier.highlight ? 'text-stone-300' : 'text-stone-500'}`}>/month</span>
      </div>
      {billing === 'annual' && (
        <p className={`text-[11px] mt-1 font-semibold ${tier.highlight ? 'text-emerald-400' : 'text-emerald-600'}`}>
          Billed annually • 20% off
        </p>
      )}

      <button
        data-testid={`pricing-page-cta-${tier.name.toLowerCase()}`}
        onClick={() => pricingEnabled && startCheckout(tier.name)}
        disabled={!pricingEnabled}
        className={`mt-6 px-5 py-3 rounded-lg text-[13px] font-bold transition inline-flex items-center justify-center gap-2 ${
          !pricingEnabled
            ? 'bg-stone-200 text-stone-500 cursor-not-allowed'
            : tier.highlight
              ? 'bg-[#F97316] text-white hover:bg-[#EA580C]'
              : 'bg-stone-900 text-white hover:bg-stone-800'
        }`}
      >
        {pricingEnabled ? (
          <>{tier.cta} <ArrowRight size={14} /></>
        ) : (
          <>Coming soon <Lock size={13} /></>
        )}
      </button>

      <ul className="mt-6 space-y-2.5">
        {tier.features.map((f, i) => (
          <li key={i} className="flex items-start gap-2 text-[13px]">
            <Check size={15} className={`mt-0.5 flex-shrink-0 ${tier.highlight ? 'text-[#F97316]' : 'text-emerald-600'}`} strokeWidth={2.5} />
            <span className={tier.highlight ? 'text-stone-200' : 'text-stone-700'}>{f}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const PricingPage = () => {
  const [billing, setBilling] = useState('monthly');
  const pricingEnabled = usePricingEnabled();

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <Header />
      <main>
        <section className="relative overflow-hidden pt-16 md:pt-24 pb-16">
          <div className="absolute inset-0 dot-grid-bg opacity-50"></div>
          <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-[#F97316] opacity-10 blur-3xl"></div>

          <div className="relative max-w-[1300px] mx-auto px-6 lg:px-10 text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-stone-200 shadow-sm text-[12px] font-semibold text-stone-700 mb-6">
              <Sparkles size={13} className="text-[#F97316]" />
              Roughly 30% cheaper than the closest alternative
            </div>
            <h1 className="text-[44px] md:text-[60px] font-extrabold text-stone-900 leading-[1.05] max-w-3xl mx-auto">
              Pick a plan. Move up the map.
            </h1>
            <p className="mt-5 text-[17px] text-stone-600 max-w-xl mx-auto">
              Every plan includes the audit engine, the AI drafting queue, and the Ambanji Relay handoff to your CRM. The only thing that changes is how many profiles and how often we scan.
            </p>
            {pricingEnabled === false && (
              <div data-testid="pricing-beta-banner" className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#4F46E5]/10 border border-[#4F46E5]/30 text-[13px] font-semibold text-[#4F46E5]">
                <Lock size={13} /> We're in Beta — sign-ups open soon. Grab your free heatmap to get on the list.
              </div>
            )}

            {/* Billing toggle */}
            <div className="flex justify-center mt-8">
              <div className="inline-flex p-1 bg-white rounded-full border border-stone-200 shadow-sm">
                {['monthly', 'annual'].map((opt) => (
                  <button
                    key={opt}
                    onClick={() => setBilling(opt)}
                    className={`px-5 py-2 text-[13px] font-bold rounded-full transition ${
                      billing === opt ? 'bg-stone-900 text-white' : 'text-stone-600 hover:text-stone-900'
                    }`}
                  >
                    {opt === 'monthly' ? 'Monthly' : 'Annual – save 20%'}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Pricing cards */}
        <section className="pb-20">
          <div className="max-w-[1300px] mx-auto px-6 lg:px-10">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {pricingTiers.map((t) => <PricingCard key={t.name} tier={t} billing={billing} pricingEnabled={pricingEnabled} />)}
            </div>

            <div className="mt-14">
              <TrustBadges />
            </div>

            {/* Savings callout */}
            <div className="mt-14 max-w-3xl mx-auto rounded-2xl bg-white border border-stone-200 p-8 text-center shadow-sm">
              <p className="text-[12px] font-bold uppercase tracking-widest text-[#4F46E5] mb-2">The math</p>
              <h3 className="text-[22px] md:text-[26px] font-extrabold text-stone-900 leading-tight" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                5 profiles with Merchynt run about $595/mo.<br/>
                5 profiles with Ambanji: <span className="text-[#F97316]">$249/mo.</span>
              </h3>
              <p className="mt-3 text-[14px] text-stone-600">That's $4,152 back in the business each year.</p>
            </div>
          </div>
        </section>

        <Comparison />
        <FAQ />
      </main>
      <Footer />
    </div>
  );
};

export default PricingPage;
